/**
 * MFX OS — Item registry + blueprint revisions (blueprint Stage 5 / §3
 * Phase B, Release 3). Items are permanent identities; revisions are the
 * controlled spec versions production references.
 */
import { useMemo, useState } from 'react';
import { useOsList, fmtDate } from '../../os/core';
import { approveBlueprintRevision } from '../../os/commands';
import { Chip, Modal } from '../../os/ui';
import type { BlueprintRevision, Item } from '../../os/types';

export default function Items() {
  const { rows: items, loading } = useOsList<Item>('items', { order: ['createdAt', 'desc'] });
  const { rows: revs } = useOsList<BlueprintRevision>('blueprintRevisions', { limit: 800 });
  const [q, setQ] = useState('');
  const [sel, setSel] = useState<(Item & { id: string }) | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const shown = useMemo(() => {
    if (!q.trim()) return items;
    const t = q.toLowerCase();
    return items.filter((x) => `${x.internalPart} ${x.customerPart} ${x.name} ${x.clientName}`.toLowerCase().includes(t));
  }, [items, q]);

  const revsFor = (itemId: string) => revs.filter((r) => r.itemId === itemId).sort((a, b) => b.revCode.localeCompare(a.revCode));
  const drafts = revs.filter((r) => r.status === 'DRAFT').length;

  const approve = async (rev: BlueprintRevision & { id: string }) => {
    setBusy(true); setErr('');
    try { await approveBlueprintRevision(rev); } catch (e) { setErr((e as Error).message); }
    setBusy(false);
  };

  if (loading) return <div className="loading"><div className="spinner" /> Loading items…</div>;

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-title">Items & Blueprints</div>
          <div className="page-sub">Permanent product identities + controlled blueprint revisions. Production always references the exact revision used.</div>
        </div>
      </div>

      <div className="grid cols-3" style={{ marginBottom: 16 }}>
        <div className="metric-card"><div className="metric-label">Items</div><div className="metric-value" style={{ color: 'var(--ac)' }}>{items.length}</div><div className="metric-note">registered</div></div>
        <div className="metric-card"><div className="metric-label">Revisions</div><div className="metric-value">{revs.length}</div><div className="metric-note">all statuses</div></div>
        <div className="metric-card"><div className="metric-label">Draft revisions</div><div className="metric-value" style={{ color: drafts ? 'var(--yl)' : 'var(--gn)' }}>{drafts}</div><div className="metric-note">awaiting approval</div></div>
      </div>

      <div className="toolbar"><input className="search" placeholder="Search part #, name, client…" value={q} onChange={(e) => setQ(e.target.value)} /></div>

      <div className="table-wrap"><table>
        <thead><tr><th>Internal part</th><th>Name</th><th>Customer part</th><th>Client</th><th>Current rev</th><th>Revisions</th><th>Created</th></tr></thead>
        <tbody>
          {shown.map((x) => (
            <tr key={x.id} onClick={() => setSel(x)}>
              <td><strong>{x.internalPart}</strong></td>
              <td>{x.name}</td>
              <td>{x.customerPart || '—'}</td>
              <td>{x.clientName || '—'}</td>
              <td><Chip label={x.currentRevCode ? `Rev ${x.currentRevCode}` : 'none'} tone={x.currentRevCode ? 'var(--gn)' : 'var(--tx3)'} /></td>
              <td>{revsFor(x.id).length}</td>
              <td>{fmtDate(x.createdAt)}</td>
            </tr>
          ))}
          {!shown.length && <tr><td colSpan={7}><div className="empty">No items yet — they're created when sales-order lines are resolved.</div></td></tr>}
        </tbody>
      </table></div>

      <Modal open={!!sel} title={sel ? `${sel.internalPart} — ${sel.name}` : ''} onClose={() => setSel(null)} wide>
        {sel && (
          <>
            <div className="os-kv-grid">
              <div><em>Customer part</em><span>{sel.customerPart || '—'}</span></div>
              <div><em>Client</em><span>{sel.clientName || '—'}</span></div>
              <div><em>Family</em><span>{sel.family || '—'}</span></div>
              <div><em>Current revision</em><span>{sel.currentRevCode ? `Rev ${sel.currentRevCode}` : '—'}</span></div>
            </div>
            <div className="os-sect">Blueprint revisions</div>
            {revsFor(sel.id).map((r) => (
              <div key={r.id} className="os-pick" style={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginBottom: 6 }}>
                <span>
                  <strong>Rev {r.revCode}</strong> <Chip label={r.status} />
                  <span style={{ color: 'var(--tx2)', marginLeft: 6 }}>{r.reason}</span>
                  {r.approvedAt && <span style={{ color: 'var(--tx3)', marginLeft: 6 }}>approved {fmtDate(r.approvedAt)} by {r.approvedBy}</span>}
                </span>
                {r.status === 'DRAFT' && <button className="btn sm primary" disabled={busy} onClick={() => approve(r)}>✓ Approve revision</button>}
              </div>
            ))}
            {!revsFor(sel.id).length && <div className="empty">No revisions.</div>}
            {err && <div className="os-err">{err}</div>}
          </>
        )}
      </Modal>
    </>
  );
}
